import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Popover, Row, Col } from 'antd';
import {
  PaperClipOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import ProfileCircle from '../common/profile/ProfileCircle';
import ProfileAvatar from '../common/ProfileAvatar';
import Permissions from '../auth/Permissions';

import {
  positionToQuarter,
  quarterToText,
  SHIFT_BLOCK_STRETCH_HANDLE_PX,
  getTextColor,
  getBackgroundColor,
  getStatusIcon
} from './scheduleHelpers';

import { getIdMap } from '../helpers/helpers';

import colors from '../constants/Colors';
import { BOTTOM_BUFFER, QUARTER_HEIGHT } from './schedule.constants';
import ScheduleShiftHoverCard from './scheduleHoverCard/ScheduleShiftHoverCard';
import { CalendarDaysIcon } from './ScheduleLayoutToggle';

const getClassName = (isWeek) => {
  const suffix = isWeek ? 'schedule-item-week' : 'schedule-item';
  return `schedule-item-regular ${suffix}`;
};

/** Min card width (px) to show inline Draft badge beside title without crowding. */
const CARD_DRAFT_MIN_WIDTH = 150;

const FileView = ({ files = [] }) => {
  const fl = files.length;
  if(fl === 0) return null;
  return(
    <div>
      <br/>
      <PaperClipOutlined style={{ marginRight: 5 }}/>
      <span>{fl.toString()} file{fl === 1 ? '' :'s'} attached</span>
    </div>
  );
};

const UserView = ({
  name, scheduleColor, radius = 12, fontSize, zIndex,
}) => {
  if(!name) return null;
  const [first = ' ',last = ' '] = name.split(' ').filter((word) => word && word.length);
  const initials = first[0] + last[0];
  const circleStyle = {
    ...(scheduleColor ? { backgroundColor: scheduleColor } : {}),
    ...(zIndex ? { zIndex } : {}),
  };
  const textColor = scheduleColor ? getTextColor(scheduleColor) : undefined;
  return (
    <ProfileCircle
      radius={radius}
      fontSize={fontSize}
      initials={initials}
      className='schedule-item-user-circle'
      style={circleStyle}
      accentColor={textColor}
    />
  );
};

const UserListView = ({ shiftId, users = [], userMap = {}, isWeek, bodyRef }) => {
  const ul = users.length;
  if(ul === 0) return null; // Should only happen if the shift is not complete
  const fullUsers = users.map((userId) => userMap[userId] || {});
  return (
    <Popover
      placement='bottomRight'
      content={
        <div className='schedule-item-user-popover-container'>
          {
            fullUsers.map((user, idx) => (
              <div key={shiftId + user.id}>
                <ProfileAvatar
                  role={user.position}
                  name={user.name}
                />
                {idx < fullUsers.length - 1 &&
                  <div className='schedule-item-user-popover-divider'/>
                }
              </div>
            ))
          }
        </div>
      }
    >
      <div
        className={
          isWeek
          ? 'schedule-item-user-list-week'
          : 'schedule-item-user-list-day'
        }
      >
        {fullUsers.map((user) => (
          <UserView key={shiftId + user.id} {...user} hasMultiple={users.length > 1}/>
        ))}
      </div>
    </Popover>
  );
};

const ProjectInfo = ({ value, type }) => {
  if(!value) return null;
  return (
    <>
    <br/>
    {type}: {value}
    </>
  )
}

export default (props) => {
  const ourProps = {...props};
  const {
    style = {},
    isWeek,
    shift: {
      id,
      top,
      bottom,
      left,
      width,
      title,
      files,
      users,
      zIndex = 1,
      projectId,
      phaseId,
      costcodeId,
      isEvent,
      color,
      isDraft,
      appliedUsers,
      startTime,
      endTime,
      shiftCategoryId,
      categoryId,
    },
    allDay,
    shiftBlockStyle = {},
    hideShiftText,
    card,
  } = ourProps;
  delete ourProps.shift;
  delete ourProps.style;
  delete ourProps.isWeek;
  delete ourProps.hideTime;
  delete ourProps.allDay;
  delete ourProps.card;

  const bodyRef = useRef();
  const isEditing = style.cursor === 'move' || style.cursor === 'row-resize' || style.cursor === 'ns-resize';
  const [expanded, setExpanded] = useState(false);
  const [cardWidthPx, setCardWidthPx] = useState(0);

  useLayoutEffect(() => {
    if (!card) return undefined;
    const node = bodyRef.current;
    if (!node) return undefined;
    const update = () => setCardWidthPx(node.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [card, width, left, top, bottom, expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onMove = (event) => {
      const node = bodyRef.current;
      if (!node) return;
      const popup = document.querySelector('.schedule-shift-hover-popover');
      if (popup && popup.contains(event.target)) return;
      const rect = node.getBoundingClientRect();
      // The expanded card is inset 20px. A pointer on the old edge stays just
      // outside that box, so allow that margin before collapsing.
      const slop = 28;
      const inside = event.clientX >= rect.left - slop
        && event.clientX <= rect.right + slop
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
      if (!inside) setExpanded(false);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [expanded]);

  const { t } = useTranslation();
  const stateUsers = useSelector((state) => state.users.users);
  const stateProjects = useSelector((state) => state.projects.projects);
  const statePhases = useSelector((state) => state.costcodes.phases);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const categories = useSelector((state) => state.schedule.categories);
  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);
  const projectMap = useMemo(() => getIdMap(stateProjects), [stateProjects]);
  const phaseMap = useMemo(() => getIdMap(statePhases), [statePhases]);

  const isMyShift = useMemo(() => users && users.includes(Permissions.id),[users]);

  const currentUserStatus = useMemo(() => {
    if (appliedUsers && appliedUsers.length === 0) return;
    const shiftStatus = appliedUsers?.find((u) => u.userId === Permissions.id)?.status;
    return shiftStatus;
  }, [appliedUsers]);

  const projectName = useMemo(() => {
    const {
      [projectId]:{
        name,
      } = {},
    } = projectMap;
    return name;
  },[projectId,projectMap]);

  const phaseName = useMemo(() => {
    const {
      [phaseId]:{
        name,
      } = {},
    } = phaseMap;
    return name;
  },[phaseId,phaseMap]);

  const costcodeText = useMemo(() => {
    const {
      [costcodeId]:{
        code,
        name,
      } = {},
    } = costcodeIdMap;
    if(!code || !name) return null;
    if(isWeek || width <= 35) return code;
    return `${code} - ${name}`;
  },[costcodeId, costcodeIdMap, isWeek, width]);

  const backgroundColor = getBackgroundColor(users, userMap, color, isMyShift, isEvent, isDraft);

  const textColor = useMemo(() => (
    isEvent
    ? colors.ONTRACCR_BLACK
    : getTextColor(backgroundColor)
  ), [isEvent, backgroundColor]);

  const showDraftLabel = isDraft && card && cardWidthPx >= CARD_DRAFT_MIN_WIDTH;

  const body = useMemo(() => {
    const start = quarterToText(positionToQuarter(top));
    const end = quarterToText(positionToQuarter(bottom ? bottom : top + QUARTER_HEIGHT));
    const text = `${start} - ${end}`;
    const height = bottom ? bottom - top : 0;
    if (card && !allDay) {
      const compactTime = (label) => label.replace(':00', '');
      const timeLabel = `${compactTime(start)}–${compactTime(end)}`;
      const fullUsers = (users || []).map((userId) => userMap[userId] || {}).filter((user) => user.name);
      const shownUsers = fullUsers;
      const primaryName = fullUsers[0]?.name;
      const extraCount = fullUsers.length > 1 ? fullUsers.length - 1 : 0;
      const showTime = height >= 36;
      const showPeople = height >= 70 && fullUsers.length > 0;
      return (
        <div className="schedule-item-card-body">
          <div className="schedule-item-card-copy">
            <div className="schedule-item-card-title-row">
              <div className="schedule-item-card-title">
                {!!isEvent && (
                  <CalendarDaysIcon size={12} color={backgroundColor} label="Event" />
                )}
                <span className="schedule-item-card-title-text">
                  {title || timeLabel}
                  {isDraft ? getStatusIcon(currentUserStatus) : null}
                </span>
              </div>
              {!!showDraftLabel && (
                <div className="schedule-item-card-draft">Draft</div>
              )}
            </div>
            {!!showTime && !!title && (
              <div className="schedule-item-card-time">{timeLabel}</div>
            )}
            {!!showPeople && primaryName && (
              <div className="schedule-item-card-people">
                <div className="schedule-item-card-avatars">
                  {shownUsers.map((user, idx) => (
                    <UserView
                      key={id + user.id}
                      name={user.name}
                      scheduleColor={user.scheduleColor}
                      radius={8}
                      fontSize={8}
                      zIndex={shownUsers.length - idx}
                    />
                  ))}
                </div>
                <span className="schedule-item-card-people-name">
                  {`${primaryName}${extraCount ? ` +${extraCount}` : ''}`}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    if(!title) return (
      <div className='schedule-item-text' style={{ color: textColor }}>
        {text}
      </div>
    );
    const dayBreakPoints = [20,50,75,150];
    const weekBreakPoints = [20,100,150,300];
    const breakPoints = isWeek ? weekBreakPoints : dayBreakPoints;
    const heightThresh = isWeek ? 100 : 50;
    if(allDay) {
      return (
        <div
          className={card ? 'schedule-item-card-title' : 'schedule-item-text'}
          style={card ? undefined : { color: textColor }}
        >
          {!hideShiftText && (
            <>
              {!!card && !!isEvent && (
                <CalendarDaysIcon size={12} color={backgroundColor} label="Event" />
              )}
              {card ? (
                <span className="schedule-item-card-title-text">
                  {title}
                  {' '}
                  {isDraft ? getStatusIcon(currentUserStatus) : null}
                </span>
              ) : (
                <>
                  {title}
                  {' '}
                  {isDraft ? getStatusIcon(currentUserStatus) : null}
                </>
              )}
            </>
          )}
        </div>
      )
    }

    const userView = !isDraft
      ? <UserListView shiftId={id} users={users} userMap={userMap} isWeek={isWeek} bodyRef={bodyRef} />
      : null;

    const titleStyle = isWeek
      ? { maxWidth: 'calc(100% - 40px)', minWidth: 30 }
      : { maxWidth: 'calc(100% - 210px)', minWidth: 100  };


    if(height < heightThresh) {
      return (
        <div className='schedule-item-text' style={{ color: textColor }}>
          { !hideShiftText && (
            <Row justify="space-between">
            <Col flex="auto" style={titleStyle}>
              {`${title}  ${text}`} {isDraft ? getStatusIcon(currentUserStatus) : null}
            </Col>
            <Col className={isWeek ? 'schedule-user-col-week' : 'schedule-user-col-day'}>
              {userView}
            </Col>
          </Row>
          )}
      </div>
      )
    }
    return (
      <div className='schedule-item-text' style={{ color: textColor }}>
        { !hideShiftText && (
          <Row justify='space-between'>
            <Col style={titleStyle}>
              {title}
              <br/>{text}
              <br/>
              {height > breakPoints[1]
                &&
                <ProjectInfo value={projectName} type={t('Project')}/>
              }
              {height > breakPoints[2]
                &&
                <ProjectInfo value={phaseName} type='Phase'/>
              }
              {height > breakPoints[2]
                &&
                <ProjectInfo value={costcodeText} type='Cost Code'/>
              }
              {height > breakPoints[3] && <FileView files={files}/>}
            </Col>
            <Col className={isWeek ? 'schedule-user-col-week' : 'schedule-user-col-day'}>
              {
                isEvent ?
                <AlertOutlined style={{ color: textColor, position:'absolute', top: -2, right: 2 }}/>
                : null
              }
              {userView}
            </Col>
            {isDraft ?
              <Col>
                {getStatusIcon(currentUserStatus, '18px', '1rem')}
              </Col>
            : null}
          </Row>
        )}
      </div>
    );
  },[
    id,
    allDay,
    title,
    top,
    bottom,
    files,
    users,
    userMap,
    isWeek,
    projectName,
    phaseName,
    costcodeText,
    textColor,
    bodyRef,
    hideShiftText,
    currentUserStatus,
    isDraft,
    isEvent,
    backgroundColor,
    card,
    showDraftLabel,
  ]);

  const realTop = top + 10;
  const realHeight = bottom ? bottom - top - BOTTOM_BUFFER : 25;
  const titleOnly = card && (allDay || (bottom ? bottom - top : 0) < 36);

  const blockStyle = {
    top: realTop,
    height: realHeight,
    width: `${width}%`,
    left: `${left}%`,
    zIndex,
    ...style,
    ...shiftBlockStyle,
  };
  if (card) {
    blockStyle['--category-color'] = backgroundColor;
  } else {
    blockStyle.backgroundColor = backgroundColor;
    blockStyle.color = textColor;
    blockStyle.opacity = isDraft ? 0.5 : 1;
  }

  const block = (
    <div
      key={title}
      className={`${getClassName(isWeek)}${card ? ' schedule-item-card' : ''}${titleOnly ? ' schedule-item-card-title-only' : ''}${card && isDraft ? ' schedule-item-draft' : ''}${expanded ? ' schedule-item-card-expanded' : ''}`}
      ref={bodyRef}
      style={blockStyle}
      {...ourProps}
      onMouseEnter={(event) => {
        if (ourProps.onMouseEnter) ourProps.onMouseEnter(event);
        if (card && !isEditing) setExpanded(true);
      }}
    >
      {!allDay && realHeight > SHIFT_BLOCK_STRETCH_HANDLE_PX * 2 && (
        <div className='schedule-item-top-anchor'/>
      )}
      {body}
      {!allDay && realHeight > SHIFT_BLOCK_STRETCH_HANDLE_PX * 2 && (
        <div className='schedule-item-bottom-anchor'/>
      )}
    </div>
  );

  const showHoverCard = card && style.cursor !== 'move' && style.cursor !== 'row-resize';
  if (!showHoverCard) return block;

  const categoryMap = getIdMap(categories || []);
  const {
    [shiftCategoryId || categoryId]: {
      name: categoryName,
      color: categoryColor,
    } = {},
  } = categoryMap;
  const assignedUsers = (users || [])
    .map((userId) => userMap[userId] || {})
    .filter((user) => user.name);

  return (
    <ScheduleShiftHoverCard
      title={title}
      color={backgroundColor}
      startTime={startTime}
      endTime={endTime}
      allDay={allDay}
      users={assignedUsers}
      isEvent={isEvent}
      isDraft={isDraft}
      projectId={projectId}
      phaseId={phaseId}
      costcodeId={costcodeId}
      categoryName={categoryName}
      categoryColor={categoryColor}
    >
      {block}
    </ScheduleShiftHoverCard>
  );
}